import React, { useState } from 'react';
import classNames from 'classnames';

interface ProfessionalResponseFormProps {
    bookingId: number;
    conversationId: number;
    recipientId: string;
    initialDate?: string;
    initialPeriod?: string;
    initialModality?: string;
    onPropose: (bookingId: number, conversationId: number, recipientId: string, proposedDates: any[]) => Promise<void>;
    onReject: (bookingId: number, conversationId: number, recipientId: string, reason: string) => Promise<void>;
}

export const ProfessionalResponseForm: React.FC<ProfessionalResponseFormProps> = ({
    bookingId,
    conversationId,
    recipientId,
    initialDate = '',
    initialPeriod = '',
    initialModality = 'Presencial',
    onPropose,
    onReject
}) => {
    const [responseType, setResponseType] = useState<'exact' | 'alternatives'>('exact');
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [error, setError] = useState('');

    const allowedModalities = (initialModality === 'Presencial' || initialModality === 'Virtual')
        ? [initialModality]
        : ['Presencial', 'Virtual'];
    const safeInitialDate = initialDate ? initialDate.split('T')[0] : '';
    
    // Set default time based on requested period
    let defaultTime = '10:00';
    if (initialPeriod === 'Tarde') {
        defaultTime = '12:00';
    } else if (initialPeriod === 'Noche') {
        defaultTime = '18:00';
    } else if (initialPeriod === 'Mañana') {
        defaultTime = '08:00';
    }

    const defaultState = { date: safeInitialDate, time: defaultTime, mode: allowedModalities[0] };
    const [exact, setExact] = useState({ ...defaultState });
    const [alternatives, setAlternatives] = useState([{ ...defaultState }]);

    const handlePropose = async () => {
        setError('');
        const proposals = responseType === 'exact' ? [exact] : alternatives;
        
        // Validate
        for (const p of proposals) {
            if (!p.date || !p.time || !p.mode) {
                setError('Por favor, completa todos los campos de fecha y hora.');
                return;
            }
        }

        // Format dates correctly to combine date and time (e.g. "2026-08-19 10:00:00")
        const formattedProposals = proposals.map(p => `${p.date} ${p.time}:00`);

        try {
            await onPropose(bookingId, conversationId, recipientId, formattedProposals);
        } catch (e) {
            setError('Error al enviar la propuesta.');
        }
    };

    const handleReject = async () => {
        setError('');
        if (!rejectionReason.trim()) {
            setError('Debes explicar por qué no puedes aceptar.');
            return;
        }

        try {
            await onReject(bookingId, conversationId, recipientId, rejectionReason);
        } catch (e) {
            setError('Error al enviar el rechazo.');
        }
    };

    const renderAlternativeRow = (option: any, index: number, isExact: boolean) => (
        <div key={index} className={classNames('alternative-row', { single: isExact })}>
            <div className="alternative-label">{isExact ? 'Horario propuesto' : `Opción ${index + 1}`}</div>
            <div className="response-field">
                <label>Fecha{index === 0 ? ' *' : ''}</label>
                <input 
                    type="date" 
                    value={option.date} 
                    onChange={e => {
                        const val = e.target.value;
                        if (isExact) setExact(prev => ({ ...prev, date: val }));
                        else {
                            const newAlts = [...alternatives];
                            newAlts[index].date = val;
                            setAlternatives(newAlts);
                        }
                    }} 
                    required 
                />
            </div>
            <div className="response-field">
                <label>Hora{index === 0 ? ' *' : ''}</label>
                <select 
                    value={option.time} 
                    onChange={e => {
                        const val = e.target.value;
                        if (isExact) setExact(prev => ({ ...prev, time: val }));
                        else {
                            const newAlts = [...alternatives];
                            newAlts[index].time = val;
                            setAlternatives(newAlts);
                        }
                    }} 
                    required
                >
                    {["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00"].map(time => (
                        <option key={time} value={time}>{time}</option>
                    ))}
                </select>
            </div>
            <div className="response-field">
                <label>Modalidad{index === 0 ? ' *' : ''}</label>
                <select 
                    value={option.mode} 
                    onChange={e => {
                        const val = e.target.value;
                        if (isExact) setExact(prev => ({ ...prev, mode: val }));
                        else {
                            const newAlts = [...alternatives];
                            newAlts[index].mode = val;
                            setAlternatives(newAlts);
                        }
                    }} 
                    required
                >
                    {allowedModalities.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
            </div>
            {!isExact && index > 0 && (
                <button 
                    className="remove-alternative" 
                    type="button" 
                    aria-label={`Eliminar alternativa ${index + 1}`}
                    onClick={() => {
                        const newAlts = [...alternatives];
                        newAlts.splice(index, 1);
                        setAlternatives(newAlts);
                    }}
                >×</button>
            )}
        </div>
    );

    return (
        <article className="system-message">
            <div className="system-title">
                <span className="status-icon">↗</span>
                <span className="system-title-copy">
                    <strong>Responder solicitud</strong>
                    <span>La respuesta quedará registrada en este hilo.</span>
                </span>
            </div>
            <div className="response-form">
                {!rejectMode && (
                    <>
                        <div className="response-tabs">
                            <button 
                                className={classNames('response-tab', { 'is-active': responseType === 'exact' })} 
                                type="button" 
                                onClick={() => setResponseType('exact')}
                            >
                                Definir hora exacta
                            </button>
                            <button 
                                className={classNames('response-tab', { 'is-active': responseType === 'alternatives' })} 
                                type="button" 
                                onClick={() => setResponseType('alternatives')}
                            >
                                Proponer alternativas
                            </button>
                        </div>
                        <div className="alternative-list">
                            {responseType === 'exact' 
                                ? renderAlternativeRow(exact, 0, true)
                                : alternatives.map((alt, index) => renderAlternativeRow(alt, index, false))
                            }
                        </div>
                        {responseType === 'alternatives' && alternatives.length < 3 && (
                            <button 
                                className="add-alternative" 
                                type="button" 
                                onClick={() => setAlternatives(prev => [...prev, { date: '', time: defaultTime, mode: 'Presencial' }])}
                            >
                                ＋ Agregar otra opción
                            </button>
                        )}
                        <div className="chat-actions">
                            <button className="chat-button primary" type="button" onClick={handlePropose}>
                                {responseType === 'exact' ? "Enviar horario" : "Enviar alternativas"}
                            </button>
                            <button className="chat-button secondary" type="button" onClick={() => setRejectMode(true)}>
                                No puedo aceptar
                            </button>
                        </div>
                    </>
                )}

                {rejectMode && (
                    <>
                        <div className="reject-panel">
                            <label>Explicá por qué no podés aceptar *</label>
                            <textarea 
                                maxLength={500} 
                                placeholder="Este mensaje quedará visible para el paciente."
                                value={rejectionReason}
                                onChange={e => setRejectionReason(e.target.value)}
                            />
                        </div>
                        <div className="chat-actions">
                            <button className="chat-button primary" type="button" onClick={handleReject}>
                                Confirmar rechazo
                            </button>
                            <button className="chat-button secondary" type="button" onClick={() => setRejectMode(false)}>
                                Cancelar
                            </button>
                        </div>
                    </>
                )}

                {error && <p className="form-error is-visible">{error}</p>}
            </div>
        </article>
    );
};
